/**
 * MarketingHooks — /marketing/hooks
 * Hook approval surface. Marketing edits & approves outreach templates for sales.
 * Mi6 palette: black #0B0B0F · red #DC2626.
 */
import { useState, useEffect } from "react";
import MarketingShell from "@/components/marketing/MarketingShell";
import HookLibrary from "@/components/marketing/HookLibrary";
import {
  loadEvents,
  saveEvents,
  seedEventsIfEmpty,
  generateHooks,
  type MarketingEvent,
  type HookEntry,
} from "@/lib/marketing-hook-generator";
const MI6_BLACK = "#0B0B0F";
const MI6_RED  = "#DC2626";

const HOOK_LABELS: Record<string, string> = {
  invite: "Invite Hook",
  participation: "Participation Hook",
  round_table: "Round Table Hook",
};
const HOOK_DESCRIPTIONS: Record<string, string> = {
  invite: "For prospects already engaged — invite them to meet at the event.",
  participation: "For senior prospects — invite to participate as a panelist.",
  round_table: "For tight executive groups — small roundtable invitation.",
};

function HookCard({
  hookKey,
  hook,
  onChange,
  onApprove,
}: {
  hookKey: "invite" | "participation" | "round_table";
  hook: HookEntry;
  onChange: (text: string) => void;
  onApprove: () => void;
}) {
  const [editing, setEditing] = useState(hook.text);

  useEffect(() => {
    setEditing(hook.text);
  }, [hook.text]);

  return (
    <div
      className="rounded-xl border overflow-hidden"
      style={{ borderColor: hook.approved ? "#059669" : "#E5E5E7" }}
    >
      <div
        className="px-4 py-2.5 flex items-center justify-between"
        style={{ background: hook.approved ? "#059669" : MI6_BLACK }}
      >
        <div>
          <div className="text-white font-semibold text-sm">{HOOK_LABELS[hookKey]}</div>
          <div className="text-[10px] mt-0.5" style={{ color: hook.approved ? "#A7F3D0" : "#8E8E94" }}>{HOOK_DESCRIPTIONS[hookKey]}</div>
        </div>
        {hook.approved ? (
          <div className="flex items-center gap-1.5 bg-white/20 rounded-full px-3 py-1">
            <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
            <span className="text-white text-[10px] font-bold uppercase tracking-wide">Approved</span>
          </div>
        ) : (
          <span className="bg-white/10 text-white text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-full">Pending</span>
        )}
      </div>
      <div className="p-4 bg-white">
        <textarea
          className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#0B0B0F] resize-none text-neutral-700 leading-relaxed"
          rows={4}
          value={editing}
          onChange={(e) => { setEditing(e.target.value); onChange(e.target.value); }}
        />
        <div className="mt-2 text-[9px] text-neutral-500">
          Placeholders: <code className="bg-neutral-50 px-1 rounded">[Prospect Name]</code> · <code className="bg-neutral-50 px-1 rounded">{"{topic_placeholder}"}</code> · <code className="bg-neutral-50 px-1 rounded">{"{audience_description}"}</code>
        </div>
        <div className="mt-3 flex items-center justify-between gap-3">
          {hook.approved && hook.approved_at && (
            <div className="text-[9px] text-emerald-600">
              Approved {new Date(hook.approved_at).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
            </div>
          )}
          {!hook.approved && <div className="flex-1" />}
          <button
            onClick={onApprove}
            className="px-4 py-1.5 rounded-lg text-xs font-bold text-white transition-all hover:opacity-90"
            style={{ background: hook.approved ? "#6B7280" : MI6_RED }}
          >
            {hook.approved ? "Revoke Approval" : "Approve"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MarketingHooks() {
  const [events, setEvents] = useState<MarketingEvent[]>([]);
  const [saved, setSaved] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "pending" | "approved">("all");

  useEffect(() => {
    const evs = seedEventsIfEmpty();
    const withHooks = evs.map((ev) => {
      if (!ev.hooks) return { ...ev, hooks: generateHooks(ev) };
      return ev;
    });
    saveEvents(withHooks);
    setEvents(withHooks);
  }, []);

  function updateHookText(eventId: string, hookKey: "invite" | "participation" | "round_table", text: string) {
    setEvents((prev) => {
      const next = prev.map((ev) => {
        if (ev.id !== eventId || !ev.hooks) return ev;
        return { ...ev, hooks: { ...ev.hooks, [hookKey]: { ...ev.hooks[hookKey], text } } };
      });
      return next;
    });
  }

  /**
   * Sync all currently-approved hooks to the shared `aip:approved-hooks` key
   * so the BDR cadence queue can surface them as new outreach.
   */
  function syncApprovedHooksToQueue(updatedEvents: typeof events) {
    try {
      const approved: Array<{
        id: string;
        eventName: string;
        hookKey: string;
        text: string;
        approved_at: string;
      }> = [];
      for (const ev of updatedEvents) {
        if (!ev.hooks) continue;
        for (const key of ["invite", "participation", "round_table"] as const) {
          const h = ev.hooks[key];
          if (h.approved) {
            approved.push({
              id: `${ev.id}::${key}`,
              eventName: ev.name,
              hookKey: key,
              text: h.text,
              approved_at: h.approved_at || new Date().toISOString(),
            });
          }
        }
      }
      localStorage.setItem("aip:approved-hooks", JSON.stringify(approved));
    } catch {
      // localStorage unavailable — silent fail
    }
  }

  function toggleApprove(eventId: string, hookKey: "invite" | "participation" | "round_table") {
    setEvents((prev) => {
      const next = prev.map((ev) => {
        if (ev.id !== eventId || !ev.hooks) return ev;
        const hook = ev.hooks[hookKey];
        const updated: HookEntry = hook.approved
          ? { ...hook, approved: false, approved_at: undefined }
          : { ...hook, approved: true, approved_at: new Date().toISOString() };
        return { ...ev, hooks: { ...ev.hooks, [hookKey]: updated } };
      });
      saveEvents(next);
      // Push all approved hooks to the shared queue key so BDR cadence can read them.
      syncApprovedHooksToQueue(next);
      setSaved(eventId + hookKey);
      setTimeout(() => setSaved(null), 2000);
      return next;
    });
  }

  const filteredEvents = events.filter((ev) => {
    if (filter === "pending") {
      return ev.hooks && Object.values(ev.hooks).some((h) => !h.approved);
    }
    if (filter === "approved") {
      return ev.hooks && Object.values(ev.hooks).some((h) => h.approved);
    }
    return true;
  }).filter((ev) => ev.status !== "Cancelled");

  const totalHooks = events.reduce((s, ev) => s + (ev.hooks ? 3 : 0), 0);
  const approvedHooks = events.reduce((s, ev) => s + (ev.hooks ? Object.values(ev.hooks).filter((h) => h.approved).length : 0), 0);

  return (
    <MarketingShell>
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[9px] uppercase tracking-[0.24em] text-neutral-500 mb-0.5">Sales Hook Workflow</div>
            <h1 className="text-lg font-bold text-neutral-900">Outreach Hook Approval</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex gap-1 bg-neutral-100 rounded-lg p-0.5">
              {(["all","pending","approved"] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className="px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-150 capitalize"
                  style={{
                    background: filter === f ? MI6_BLACK : "transparent",
                    color: filter === f ? "white" : "#6B6B70",
                  }}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 flex-1 w-full">

        {/* Reusable hook library */}
        <div className="mb-8">
          <HookLibrary />
        </div>

        <div className="text-[9px] uppercase tracking-[0.22em] text-neutral-500 font-bold mb-3">Per-event approval workflow</div>

        {/* Summary KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-4" style={{ borderLeft: `4px solid ${MI6_BLACK}` }}>
            <div className="text-[9px] uppercase tracking-[0.18em] text-neutral-500 mb-1">Total Events</div>
            <div className="text-2xl font-bold tabular-nums" style={{ color: MI6_BLACK }}>{events.filter((e) => e.status !== "Cancelled").length}</div>
          </div>
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-4" style={{ borderLeft: `4px solid #059669` }}>
            <div className="text-[9px] uppercase tracking-[0.18em] text-neutral-500 mb-1">Hooks Approved</div>
            <div className="text-2xl font-bold tabular-nums text-emerald-600">{approvedHooks} <span className="text-sm font-normal text-neutral-500">/ {totalHooks}</span></div>
          </div>
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-4" style={{ borderLeft: `4px solid ${MI6_RED}` }}>
            <div className="text-[9px] uppercase tracking-[0.18em] text-neutral-500 mb-1">Pending Approval</div>
            <div className="text-2xl font-bold tabular-nums" style={{ color: MI6_RED }}>{totalHooks - approvedHooks}</div>
          </div>
        </div>

        {/* Instructions banner */}
        <div className="mb-5 rounded-xl border border-neutral-200 bg-white px-5 py-4 flex items-start gap-3">
          <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white text-sm font-bold" style={{ background: MI6_BLACK }}>!</div>
          <div className="text-sm text-neutral-600">
            <span className="font-semibold text-neutral-800">How this works:</span> Each event auto-generates 3 hook templates. Edit the text to match your messaging, then click <span className="font-semibold text-red-600">Approve</span> to make the hook visible to BDRs and AEs in their "Outreach Ammo" tile.
            Placeholders like <code className="bg-neutral-100 px-1 rounded text-xs">[Prospect Name]</code> are filled in by the ae.
          </div>
        </div>

        {/* Event cards */}
        {filteredEvents.length === 0 ? (
          <div className="text-center text-neutral-500 text-sm py-16">No events match this filter.</div>
        ) : (
          <div className="space-y-8">
            {filteredEvents.map((ev) => {
              if (!ev.hooks) return null;
              const approvedCount = Object.values(ev.hooks).filter((h) => h.approved).length;
              return (
                <div key={ev.id} className="bg-white rounded-2xl border border-neutral-200 shadow-sm overflow-hidden">
                  {/* Event header */}
                  <div className="px-5 py-4 border-b border-neutral-100 flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <div className="font-bold text-neutral-800 text-base">{ev.name}</div>
                      <div className="text-xs text-neutral-500 mt-0.5">
                        {ev.activityType} · {new Date(ev.startDate).toLocaleDateString("en-US", { month: "long", day: "numeric" })}
                        {ev.endDate ? `–${new Date(ev.endDate).toLocaleDateString("en-US", { day: "numeric", year: "numeric" })}` : `, ${new Date(ev.startDate).getFullYear()}`}
                        {ev.location ? ` · ${ev.location}` : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${approvedCount === 3 ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : approvedCount > 0 ? "bg-orange-50 text-orange-700 border border-orange-200" : "bg-neutral-100 text-neutral-500 border border-neutral-200"}`}>
                        {approvedCount}/3 hooks approved
                      </span>
                    </div>
                  </div>

                  {/* 3 hook cards */}
                  <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
                    {(["invite","participation","round_table"] as const).map((hookKey) => (
                      <HookCard
                        key={hookKey}
                        hookKey={hookKey}
                        hook={ev.hooks![hookKey]}
                        onChange={(text) => updateHookText(ev.id, hookKey, text)}
                        onApprove={() => toggleApprove(ev.id, hookKey)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </MarketingShell>
  );
}
